import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {InputBase, type SxProps, type Theme} from '@mui/material';

interface Props {
    value?: number;
    onCommit: (value: number | undefined) => void;
    min?: number;
    max?: number;
    allowEmpty?: boolean;
    disabled?: boolean;
    stopPropagation?: boolean;
    onActivate?: () => void;
    sx?: SxProps<Theme>;
}

const FloorPlanInlineNumberInput: React.FC<Props> = ({
                                                         value,
                                                         onCommit,
                                                         min,
                                                         max,
                                                         allowEmpty = false,
                                                         disabled = false,
                                                         stopPropagation = false,
                                                         onActivate,
                                                         sx,
                                                     }) => {
    const [localValue, setLocalValue] = useState(value != null ? String(value) : '');

    useEffect(() => {
        setLocalValue(value != null ? String(value) : '');
    }, [value]);

    const parsedValue = useMemo(() => {
        if (!localValue.trim()) return undefined;
        const parsed = parseInt(localValue, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    }, [localValue]);

    const isInvalid = useMemo(() => {
        if (!localValue.trim()) return !allowEmpty;
        if (parsedValue == null) return true;
        if (min != null && parsedValue < min) return true;
        if (max != null && parsedValue > max) return true;
        return false;
    }, [allowEmpty, localValue, max, min, parsedValue]);

    const handleInteraction = useCallback((
        event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
        selectText = false,
    ) => {
        if (stopPropagation) {
            event.stopPropagation();
        }
        onActivate?.();
        if (selectText) {
            event.currentTarget.select();
        }
    }, [onActivate, stopPropagation]);

    const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (stopPropagation) {
            event.stopPropagation();
        }

        if (isInvalid) {
            setLocalValue(value != null ? String(value) : '');
            return;
        }

        const nextValue = parsedValue;
        if (nextValue !== value) {
            onCommit(nextValue);
        } else {
            setLocalValue(value != null ? String(value) : '');
        }
    }, [isInvalid, onCommit, parsedValue, stopPropagation, value]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (stopPropagation) {
            event.stopPropagation();
        }
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    }, [stopPropagation]);

    return (
        <InputBase
            value={localValue}
            onChange={(event) => setLocalValue(event.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            type="text"
            size="small"
            disabled={disabled}
            inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                onFocus: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => handleInteraction(event, true),
                onClick: (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => handleInteraction(event, true),
            }}
            sx={[
                {
                    width: '100%',
                    height: 28,
                    '& input': {
                        textAlign: 'center',
                        p: '0 4px',
                        fontSize: '0.8125rem',
                        MozAppearance: 'textfield',
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                    },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                },
                {
                    borderColor: isInvalid ? 'error.main' : 'divider',
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        />
    );
};

export default FloorPlanInlineNumberInput;
